import { cn } from '@execonline-inc/execonline-ui';
import { fromEmpty } from 'maybeasy';
import { observer } from 'mobx-react';
import { fromArrayMaybe } from 'nonempty-list';
import ReactHtmlParser from 'react-html-parser';
import { emptyFragment } from '../../../../../../EmptyFragment';
import { htmlANodeDecoder, htmlImgNodeDecoder } from './HtmlDecoders';
import HtmlImageView from './HtmlImageView';
import './styles.css';

interface Props {
  className?: string;
  description: string;
}

export interface HtmlParserNode {
  name: 'a' | 'img';
  children: [{ data: string }];
  attribs: {
    href?: string;
    src?: string;
  };
}

function HtmlView({ description, className }: Props) {
  const transform = (node: HtmlParserNode, index: number) => {
    switch (node.name) {
      case 'img':
        return htmlImgNodeDecoder.decodeAny(node).cata({
          Err: emptyFragment,
          Ok: (data) => {
            return <HtmlImageView key={index} src={data.attribs.src} />;
          },
        });
      case 'a':
        return htmlANodeDecoder.decodeAny(node).cata({
          Err: emptyFragment,
          Ok: (data) => (
            <a
              key={index}
              className="text-primary underline cursor-pointer"
              href={data.attribs.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {fromArrayMaybe(node.children)
                .map((children) => children.first.data)
                .getOrElseValue(data.attribs.href)}
            </a>
          ),
        });
    }
  };

  return fromEmpty(description)
    .map((text) => (
      <div
        className={cn(
          'text-font-size-md text-foreground xl:text-font-size-lg whitespace-pre-wrap',
          '[&_ol]:list-decimal [&_ol]:mb-3.5 [&_ol]:pl-10',
          '[&_ul]:list-disc [&_ul]:mb-3.5 [&_ul]:pl-10',
          '[&_h1]:text-4xl [&_h1]:font-bold [&_h1]:my-4',
          '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:my-3 [&_h2]:normal-case',
          '[&_h3]:text-lg [&_h3]:font-bold [&_h3]:my-4',
          '[&_p]:text-base [&_p]:font-light [&_p]:my-4 [&_p]:whitespace-pre-wrap',
          className,
        )}
      >
        {ReactHtmlParser(text, { transform })}
      </div>
    ))
    .getOrElse(emptyFragment);
}

export default observer(HtmlView);
